import type { FactSubject, MemoryFact } from "../api/memory";
import type { RosterMember } from "../api/roster";
import { ChipGroup, type ChipOption, FilterDisclosure } from "./Chips";

/**
 * **Whose:** — the memory screen's filter (issue #329, `Memory.dc.html`).
 *
 * ## It is presentation and it must stay presentation
 *
 * The filter narrows **what is rendered** and nothing else. No request gains a parameter, no
 * read is scoped, and `Memory.tsx` fetches exactly the same thing whichever chip is lit —
 * asserted in `tests/memory.test.tsx`, because that is the property this component is only
 * safe with.
 *
 * A filter that narrowed the *read* would be a second projection of the family's record, and
 * therefore a second place *"that's the lot; nothing is held back"* could stop being true.
 * That sentence is the whole reason this screen exists; #235's export makes the same argument
 * for the same reason (*"a cursor is a continuation, not a filter"*).
 *
 * ## The chips and the disclosure line are `Chips.tsx`'s
 *
 * Since #310 there is a second filter row in the panel — the list screen's — so the control,
 * the frozen treatment and the sentence under it moved to a component both screens use. That
 * sentence is why offering the control is safe at all, and it is worth keeping in view here:
 * without it a chip labelled *Dana's* reads as a **permission**, and #73 (member-scoped
 * visibility) is still an open spike.
 *
 * What stays here is the part that is about *facts*: which chips exist, and what each admits.
 *
 * ## Who gets a chip
 *
 * The active roster, always, so the row is stable while the record loads and a member with
 * nothing saved is visibly a member with nothing saved. Plus two chips that are not people:
 * *Not recorded*, for #92's third case, and *Someone who has left* — which appears only when
 * a loaded fact actually has one, because a chip nobody could ever match is noise.
 *
 * **Everyone who has left shares one chip**, which is the one place this is coarser than the
 * screen: two departed members' facts land together. The roster read is active-only (ADR 0019
 * decision 5 keeps a departed member's attribution on the record but not the member), so the
 * panel has no name to tell them apart with — and grouping them is better than the
 * alternative, which is facts no chip can reach on a screen whose whole claim is completeness.
 */

/**
 * The selected chip.
 *
 * A plain string rather than a union with a payload, because the two vocabularies cannot
 * collide: a `userId` is a ULID (`[0-9A-Z]{26}`, #119), and every literal below is lowercase.
 */
export type Whose = string;

export const EVERYONE: Whose = "all";
const FAMILY: Whose = "family";
const UNSTATED: Whose = "unstated";
const LEFT: Whose = "left";

/** The chips this screen offers — `Chips.tsx`'s shape, named for what it holds here. */
export type WhoseOption = ChipOption;

/** Does this fact belong under this chip? */
export function whoseMatches(fact: MemoryFact, whose: Whose): boolean {
  if (whose === EVERYONE) return true;
  if (whose === FAMILY) return fact.subject.kind === "family";
  if (whose === UNSTATED) return fact.subject.kind === "unstated";
  if (whose === LEFT) return fact.subject.kind === "member" && fact.subject.name === null;
  return fact.subject.kind === "member" && fact.subjectMemberId === whose;
}

/**
 * Would a fact **about to be saved** under this subject show up under the current chip?
 *
 * The write's own subject rather than a stored fact's, which is why it is a second predicate
 * and not {@link whoseMatches}: `FactSubject` is what `remember` takes (`family` or a named
 * member, #92) and carries neither of the two states a *stored* fact can be in. That is the
 * whole answer for the two chips it returns `false` for — a fact being written now is never
 * *unstated*, because the form refuses to send without an answer, and its member is never
 * *someone who has left*, because the picker only offers the active roster.
 */
export function whoseAdmits(subject: FactSubject, whose: Whose): boolean {
  if (whose === EVERYONE) return true;
  if (whose === FAMILY) return subject.kind === "family";
  if (whose === UNSTATED || whose === LEFT) return false;
  return subject.kind === "member" && subject.memberId === whose;
}

/**
 * The chips, in the canvas's order: everyone, the family, each member, then the two that are
 * not people.
 */
export function whoseOptions(
  members: readonly RosterMember[],
  facts: readonly MemoryFact[],
): readonly WhoseOption[] {
  const anyDeparted = facts.some(
    (fact) => fact.subject.kind === "member" && fact.subject.name === null,
  );
  return [
    { value: EVERYONE, label: "Everyone's" },
    { value: FAMILY, label: "The family's" },
    ...members.map((member) => ({ value: member.userId, label: `${member.name}'s` })),
    ...(anyDeparted ? [{ value: LEFT, label: "Someone who has left", faint: true }] : []),
    { value: UNSTATED, label: "Not recorded", faint: true },
  ];
}

export function WhoseFilter({
  value,
  options,
  disabled = false,
  onChange,
}: {
  value: Whose;
  options: readonly WhoseOption[];
  /** A form or a confirmation is open — see `Memory.tsx` for why the chips wait for it. */
  disabled?: boolean;
  onChange: (next: Whose) => void;
}) {
  return (
    <div className="whose">
      <ChipGroup
        legend="Whose:"
        value={value}
        options={options}
        disabled={disabled}
        onChange={onChange}
      />
      <FilterDisclosure frozen={disabled} />
    </div>
  );
}
